<template>
  <main>
    <section
      id="page-header"
      class="grid grid-cols-12 items-center justify-center lg:justify-between gap-5 mt-5 py-5 px-2"
    >
      <header
        class="text-center md:text-start text-2xl font-bold col-span-8 flex items-center"
      >
        <span>
          {{
            $t("labels.generic", { pt: "Todos os cursos", en: "All courses" })
          }}
        </span>
      </header>

      <div class="flex items-center gap-3 col-span-12 lg:col-span-4">
        <span class="p-input-icon-right w-full">
          <InputText
            type="text"
            v-model="params.name"
            :placeholder="$t('course.search')"
            class="p-inputtext-sm"
            @keyup="onFilter"
            removable
          />
          <i class="pi pi-search" />
        </span>
        <Button
          icon="pi pi-filter"
          class="btn-primary p-button-sm scale-90 w-[140px]"
          label="Filtros"
          @click.prevent="dialogFilter = true"
        />
      </div>
    </section>

    <section
      v-if="
        filters?.name ||
        filters?.done ||
        filters?.done === false ||
        filters?.classroomName
      "
      class="flex items-center gap-2 py-5 font-bold"
    >
      <p>{{ "Filtros aplicados:" }}</p>

      <Chip
        v-if="filters?.name"
        :label="filters.name"
        @remove="onRemoveFilter('name')"
        removable
      />

      <Chip
        v-if="filters?.done || filters?.done === false"
        :label="`${
          filters.done ? courseOptions[0].name : courseOptions[1].name
        }`"
        @remove="onRemoveFilter('done')"
        removable
      />

      <Chip
        v-if="filters?.classroomName"
        :label="`Turma: ${filters.classroomName}`"
        @remove="onRemoveFilter('classroom')"
        removable
      />
    </section>

    <Loader v-if="loader" :loader="loader" />

    <EmptyData
      v-else-if="courses?.length === 0"
      :data="courses?.length === 0"
      :label="emptyState.description"
      :image="emptyState.image"
      imgSize="h-40 md:h-64 mt-20"
    />

    <section class="my-12 grid gap-y-8 px-2" v-else>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <Card
          v-motion
          :initial="initialMotion"
          :enter="enterMotion.getEnterMotion((i + 1) * 100)"
          class="group bg-white !rounded-2xl !shadow-md hover:!shadow-xl transition-all duration-200 overflow-hidden cursor-default"
          v-for="(course, i) in courses"
          :key="i"
        >
          <template #header>
            <div class="relative rounded-t-lg overflow-hidden">
              <div
                v-if="course?.done"
                id="tag-status"
                class="absolute top-3 right-3 px-2 py-1 text-sm rounded-full font-bold shadow-md flex items-center gap-2 bg-green-500 text-green-100 z-40"
              >
                <span>{{ "Concluído" }}</span>
              </div>

              <img
                :src="course?.file?.url || emptyImg"
                @error="imgDefaultError"
                class="w-full h-64 object-cover transform group-hover:scale-105 transition-transform duration-200"
                loading="lazy"
              />
            </div>
          </template>
          <template #title>
            <div class="flex justify-between items-center">
              <h5 class="truncate max-w-fit" :title="course.name">
                {{ course.name }}
              </h5>
              <div class="bg-h-primary-700 px-2 rounded-xl">
                <p class="text-[10px] text-h-primary-50 truncate">
                  {{ generics.getDurationTime(Number(course.duration)) }}
                </p>
              </div>
            </div>
            <p
              v-if="course?.classroom"
              class="text-gray-400 absolute truncate max-w-[300px]"
            >
              {{ "Turma: " + course?.classroom?.name }}
            </p>
          </template>

          <template #footer>
            <div class="flex flex-wrap justify-end gap-3 -mt-5">
              <Button
                class="btn-primary w-full lg:w-fit p-button-sm flex gap-2 items-center justify-center"
                @click="goToCourse(course)"
              >
                <v-icon name="md-playcirclefilled-twotone"></v-icon>
                <span>
                  {{
                    $t("labels.generic", {
                      pt: "Acessar curso",
                      en: "Access course",
                    })
                  }}
                </span>
              </Button>

              <Button
                v-if="course.done && course?.hasCertificate"
                class="btn-outline w-full lg:w-fit p-button-sm flex justify-center content-center"
                :loading="loaderCert[i]?.value"
                @click="getCertificate(course, i)"
              >
                <v-icon
                  v-if="loaderCert[i]?.value"
                  name="md-changecircle-round"
                  :class="['animate-spin']"
                ></v-icon>
                <v-icon v-else name="md-filedownload-round"></v-icon>
                <p class="ml-1">
                  {{
                    $t("labels.generic", {
                      pt: "Certificado",
                      en: "Certificate",
                    })
                  }}
                </p>
              </Button>
            </div>
          </template>
        </Card>
      </div>
    </section>
    <div
      v-if="params.total > params?.size"
      class="flex justify-center items-center mb-10"
    >
      <Paginator
        :rows="params?.size"
        :totalRecords="params?.total"
        @page="paginate($event)"
      ></Paginator>
    </div>

    <Dialog
      v-model:visible="dialogFilter"
      header="Selecione os filtros"
      :style="{ width: '25vw' }"
      :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
      :draggable="false"
      modal
    >
      <form @submit.prevent="onFilter" class="flex flex-col gap-5">
        <div id="classroom-input" class="font-bold">
          <label for="classroom">
            {{ $t("homeStudent.filters.classroom.title") }}
          </label>
          <AutoComplete
            id="classroom"
            v-model="params.classroomName"
            :suggestions="classrooms"
            :forceSelection="true"
            @complete="getClasses($event)"
            :placeholder="$t('homeStudent.filters.classroom.placeholder')"
            optionLabel="name"
            :class="`w-full mt-2`"
          />
        </div>

        <div id="status-input" class="font-bold">
          <label for="status">
            {{ $t("homeStudent.filters.status.title") }}
          </label>
          <Dropdown
            id="status"
            v-model="params.done"
            optionLabel="name"
            optionValue="code"
            :placeholder="$t('homeStudent.filters.status.placeholder')"
            :class="`dropdown w-full mt-2`"
            :options="courseOptions"
          />
        </div>

        <div id="buttons" class="flex justify-end gap-3 mt-5">
          <Button
            :label="$t('labels.cancel')"
            class="btn-outline"
            @click.prevent="dialogFilter = false"
          />
          <Button
            type="submit"
            :label="$t('labels.apply')"
            :disabled="
              params.done === undefined && params.classroomName === undefined
            "
            class="btn-primary"
          />
        </div>
      </form>
    </Dialog>
  </main>
</template>

<script lang="ts">
import { Classroom } from "@/models/classrooms.model";
import { Course, CourseType, Status } from "@/models/courses.model";
import {
  MotionEnter,
  MotionInitial,
  MotionTransition,
} from "@/models/motion.model";
import { QueryParams } from "@/models/query-params.model";
import { User } from "@/models/user.model";
import { useCourseStudentStore } from "@/store/course-student";
import { GenericsUtils } from "@/utils/generics.util";
import { GoToRoute } from "@/utils/go-to.util";
import HandleError from "@/utils/handle-errors.util";
import { getUserFromStorage } from "@/utils/user-store.util";
import { ClassesService } from "@/views/Classes/Content/classes.service";
import { defineComponent } from "vue";
import { CourseStudentService } from "../course-student.service";
import InteractiveContent from "../InteractiveContent/index.vue";
import { setItemStorage } from "@/utils/save-storage.util";

export default defineComponent({
  name: "courseHome",
  components: { InteractiveContent },
  computed: {
    generics(): GenericsUtils {
      return new GenericsUtils();
    },
    userId(): string {
      return getUserFromStorage()?.id;
    },
    service(): CourseStudentService {
      return new CourseStudentService();
    },
    serviceClassrooms(): ClassesService {
      return new ClassesService();
    },
    errors(): HandleError {
      return new HandleError();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
  },
  watch: {
    loader(val) {
      this.loader = val;
    },
  },
  data() {
    return {
      courses: [] as Course[],
      classrooms: [] as Classroom[],
      params: new QueryParams(0, 12) as any,
      filters: {} as QueryParams,
      dialogFilter: false as boolean,
      user: {} as User,
      emptyImg:
        "https://video-thumb.s3.sa-east-1.amazonaws.com/65197bdbplaceholder.png" as string,
      emptyState: {
        image: "empty_courses.svg",
        description:
          "Aqui estarão listados os cursos em que você estiver matriculado(a).",
      } as any,
      loader: true as boolean,
      loaderCert: [] as { value: boolean }[],
      initialMotion: new MotionInitial(),
      enterMotion: new MotionEnter(1, 0, 0, new MotionTransition(30)),
      url: "" as string,
      status: Status.PUBLISHED.concat(",").concat(Status.NOT_LISTED) as string,
      courseOptions: [
        {
          name: "Concluídos",
          code: true,
        },
        {
          name: "Não concluídos",
          code: false,
        },
      ] as {
        name: string;
        code?: boolean;
      }[],
      debounceTimer: null as any,
      visible: false as boolean,
    };
  },
  mounted() {
    if (getUserFromStorage()) this.coursesPaginated();
  },
  methods: {
    paginate(event: { page: number; rows: number }): void {
      this.loader = true;
      this.setPage({ ...this.params, page: event.page, size: event.rows });
    },
    setPage(queryParams: QueryParams): void {
      this.loader = true;
      this.courses = [];
      window.scrollTo(0, 0);

      this.service
        .getCoursesByUser(this.userId, queryParams)
        .then((response) => {
          this.courses = response.content;
          this.params.total = response.total;
          this.loader = false;
          this.filters.done = this.params.done;
          this.filters.classroomName =
            this.params.classroomName?.name ?? this.params.classroomName;
          this.filters.name = this.params.name;
        })
        .finally(() => (this.loader = false));
    },
    coursesPaginated(): void {
      this.params.status = this.status;
      this.params.classroomName =
        this.params.classroomName?.name ?? this.params.classroomName;
      this.setPage(this.params);
    },
    goToCourse(course: Course): void {
      const params = {
        courseName: course?.name?.replaceAll(" ", "-")?.toLowerCase(),
        id: course?.id,
      };

      window.scrollTo(0, 0);

      if (course.type === CourseType.ADAPTATIVE) {
        this.go.redirectToPage("courseContent", params);
        setItemStorage("courseName", params.courseName);
      } else if (course.type === CourseType.LINEAR) {
        this.go.redirectToPage("traditionalCourseContent", params);
        setItemStorage("courseName", params.courseName);
      } else {
        useCourseStudentStore.setData(course);
        useCourseStudentStore.setH5pId(String(course.h5pId));
        this.go.redirectToPage("interactiveCourseContent", params);
      }

      localStorage.setItem("course-type", JSON.stringify(course?.type));
      useCourseStudentStore.setCourseType(course?.type!);

      if (course.classroom === null) localStorage.removeItem("classId");
      else localStorage.setItem("classId", course.classroom?.id!);

      if (course.classroom === null) localStorage.removeItem("className");
      else localStorage.setItem("className", course.classroom?.name!);
      localStorage.setItem("courseID", JSON.stringify(course?.id));
    },
    imgDefaultError(e: any): void {
      e.target.src =
        "https://video-thumb.s3.sa-east-1.amazonaws.com/65197bdbplaceholder.png";
    },
    getCertificate(course: Course, index: number): void {
      this.loaderCert[index] = { value: true };
      this.service.certificate.subscribe((response) => {
        const blob = new Blob([response], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        this.url = url;
        this.openPDF();
        this.loader = false;
        this.loaderCert[index] = { value: false };
      });
      this.service.getCourseCertificate(
        course.id,
        this.userId,
        course?.classroom?.id ?? undefined
      );
    },
    openPDF(): void {
      window.open(
        this.url,
        this.$t("labels.generic", {
          pt: "meu-certificado.pdf",
          en: "my-certificate.pdf",
        })
      );
    },
    onFilter(): void {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.coursesPaginated(), 500);
      this.dialogFilter = false;
    },
    onRemoveFilter(param: "name" | "classroom" | "done"): void {
      if (param === "name") this.params.name = undefined;
      else if (param === "done") this.params.done = undefined;
      else if (param === "classroom") this.params.classroomName = undefined;
      this.coursesPaginated();
    },
    getClasses(event): void {
      const { query } = event;
      this.serviceClassrooms.classes.subscribe({
        next: (response: any) => {
          this.classrooms = response.content;
        },
      });
      this.serviceClassrooms.getAllClasses({ page: 0, size: 30, name: query });
    },
  },
});
</script>
